import { COLOR_PALETTE_MAP, ColorTypes, LEGACY_TO_SEMANTIC_MAPPING } from './constants'

interface ColorInfo {
  variableFormat: string
  classNameFormat: string
}

const CSS_VARIABLE_PREFIX = '--color-semantic-'
const CSS_CLASS_PREFIX = 'semanticColor'

const toKebabCase = (str: string): string => {
  if (str === '' || str == null) {
    return ''
  }
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .toLowerCase()
    .replace(/\s+/g, '-')
}

const toClassNameFormat = (str: string): string => {
  return str.replace(/\s+/g, '').replace(/\|/g, '')
}

function createColorInfo(color: string): ColorInfo {
  return {
    variableFormat: toKebabCase(color),
    classNameFormat: toClassNameFormat(color),
  }
}

function isSemanticColor(color: ColorTypes): boolean {
  return color in COLOR_PALETTE_MAP.semantic
}

function hasLegacyMapping(color: ColorTypes): boolean {
  return LEGACY_TO_SEMANTIC_MAPPING[color] !== undefined
}

function getMappedSemanticColor(color: ColorTypes): string | undefined {
  return LEGACY_TO_SEMANTIC_MAPPING[color]
}

function getColorInfo(color: ColorTypes): ColorInfo {
  if (isSemanticColor(color)) {
    return createColorInfo(color)
  }

  const mappedSemanticColor = getMappedSemanticColor(color)
  if (mappedSemanticColor != null) {
    const colorInfo = createColorInfo(mappedSemanticColor)
    warnDeprecatedColor(color, mappedSemanticColor)
    return colorInfo
  }

  const fallbackColor = COLOR_PALETTE_MAP.semantic.Prominent
  const colorInfo = createColorInfo(fallbackColor)
  warnFallbackColor(color)
  return colorInfo
}

function getColorVariable(color: ColorTypes): string {
  const colorInfo = getColorInfo(color)
  return `var(${CSS_VARIABLE_PREFIX}${colorInfo.variableFormat})`
}

function getColorClassName(color: ColorTypes): string {
  const colorInfo = getColorInfo(color)
  return `${CSS_CLASS_PREFIX}${colorInfo.classNameFormat}`
}

let showDeprecationWarnings = false
const warnedColors = new Set<string>()

export function enableDeprecationWarnings(): void {
  showDeprecationWarnings = true
}

export function disableDeprecationWarnings(): void {
  showDeprecationWarnings = false
}

export function isDeprecationWarningEnabled(): boolean {
  return showDeprecationWarnings
}

function warnDeprecatedColor(color: ColorTypes, mappedColor: string): void {
  const colorStr = String(color)
  if (showDeprecationWarnings && !warnedColors.has(colorStr)) {
    console.warn(
      `🎨 Deprecation Warning: Color "${colorStr}" is legacy and has been mapped to "${mappedColor}". Please update to use "${mappedColor}" directly.`
    )
    warnedColors.add(colorStr)
  }
}

function warnFallbackColor(color: ColorTypes): void {
  const colorStr = String(color)
  if (showDeprecationWarnings && !warnedColors.has(colorStr)) {
    console.warn(`🎨 Warning: Color "${colorStr}" has no semantic mapping and will fallback to Prominent.`)
    warnedColors.add(colorStr)
  }
}

export function resetWarningSystem(): void {
  showDeprecationWarnings = false
  warnedColors.clear()
}

export function resetWarnedColors(): void {
  warnedColors.clear()
}

export { getColorClassName, getColorVariable, getMappedSemanticColor, hasLegacyMapping, isSemanticColor }
